import { purchaseModelCode } from '@otovo/shared/cloudEnums';
import {
  Sanity$Product,
  Sanity$ProductSectionDisplayType,
} from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import ProductCardFeatures from './ProductCardFeatures';
import ProductImageCarousel from './ProductImageSlider';
import SingleProductPriceCard from './SingleProductPriceCard';

type PriceCardProps = {
  product: Sanity$Product;
  displaySubscriptionPrice: boolean;
  productSectionDisplayType: Sanity$ProductSectionDisplayType;
  numberOfProducts: number;
};

const formatPriceRange = (lowestPrice: number, highestPrice: number) => {
  if (lowestPrice && highestPrice) {
    return `${lowestPrice} - ${highestPrice} € `;
  }

  if (lowestPrice || highestPrice) {
    return `${lowestPrice || highestPrice} € `;
  }

  return '';
};

const PriceCard = ({
  product,
  displaySubscriptionPrice,
  productSectionDisplayType,
  numberOfProducts,
}: PriceCardProps) => {
  const priceContent =
    displaySubscriptionPrice &&
    productSectionDisplayType !== purchaseModelCode.BUY_NOW ? (
      <>
        {formatPriceRange(
          product.subscriptionValueLowest,
          product.subscriptionValueHighest,
        )}
        {product.subscriptionLabel}
      </>
    ) : (
      <>
        {formatPriceRange(
          product.buyNowValueLowest,
          product.buyNowValueHighest,
        )}
      </>
    );

  if (numberOfProducts === 1) {
    return (
      <SingleProductPriceCard
        product={product}
        displaySubscriptionPrice={displaySubscriptionPrice}
        priceContent={priceContent}
      />
    );
  }

  return (
    <div
      className={cx(
        'relative flex flex-1 flex-col items-center justify-center rounded-lg bg-white',
        `${
          product.mostPopular
            ? 'z-10 border-4 border-peach-50'
            : 'border border-grey-20'
        }`,
        `${product.mostPopular && numberOfProducts > 2 && 'md:scale-110'}`,
      )}
    >
      {product.mostPopular && (
        <div className="absolute -top-5 w-max rounded-full bg-peach-50 px-6 py-2 text-sm  text-grey-90">
          {product.mostPopularLabel}
        </div>
      )}
      <div className="mt-4 w-full p-8 text-center">
        <div className="text-lg text-grey-60">{priceContent}</div>

        <div className="mb-4 mt-2 text-3xl font-bold text-grey-90">
          {product.productTier}
        </div>

        {product.productImages && (
          <ProductImageCarousel productImages={product.productImages} />
        )}

        <div className="px-4 text-grey-45">{product.fullProductName}</div>
      </div>
      <div
        className={cx(
          'flex h-full w-full flex-col justify-center bg-lilac-5 text-grey-60',
          'rounded-b-xl p-4 py-6 md:p-8',
        )}
      >
        <ProductCardFeatures
          product={product}
          displaySubscriptionPrice={displaySubscriptionPrice}
        />
      </div>
    </div>
  );
};

export default PriceCard;
